import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SimpleSelect } from "@/components/Filters";
import { COURIERS } from "@/components/shipments/ShipCommon";
import { api, errMsg } from "@/lib/api";
import { todayStr } from "@/lib/format";

const EMPTY = { order_id: "", order_date: todayStr(), ship_date: "", customer_name: "", phone: "", product: "", courier: "",
  tracking_id: "", status: "auto", expected_delivery: "", remarks: "" };

const F = ({ label, children, className = "" }) => <div className={`space-y-1.5 ${className}`}><Label>{label}</Label>{children}</div>;

export const AddShipmentDialog = ({ open, onOpenChange, statuses, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));
  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, status: f.status === "auto" ? null : f.status };
    ["order_date", "ship_date", "expected_delivery"].forEach((k) => { if (!body[k]) body[k] = null; });
    try {
      const { data } = await api.post("/shipments", body);
      toast.success(data.message);
      setF({ ...EMPTY, order_date: todayStr() });
      onSaved(data.id);
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto app-light" data-testid="add-shipment-dialog">
        <DialogHeader><DialogTitle>Add shipment manually</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <F label="Order ID *"><Input required value={f.order_id} onChange={set("order_id")} className="font-mono" placeholder="403-1234567-1234567 or 1234" data-testid="add-order-id" /></F>
          <F label="Order date"><Input type="date" value={f.order_date} onChange={set("order_date")} data-testid="add-order-date" /></F>
          <F label="Customer name"><Input value={f.customer_name} onChange={set("customer_name")} data-testid="add-customer" /></F>
          <F label="Phone"><Input value={f.phone} onChange={set("phone")} inputMode="tel" data-testid="add-phone" /></F>
          <F label="Product" className="sm:col-span-2"><Input value={f.product} onChange={set("product")} placeholder="Join several products with +" data-testid="add-product" /></F>
          <F label="Courier">
            <Input list="add-courier-list" value={f.courier} onChange={set("courier")} placeholder="Auto from AWB if empty" data-testid="add-courier" />
            <datalist id="add-courier-list">{COURIERS.map((c) => <option key={c} value={c} />)}</datalist>
          </F>
          <F label="AWB / Tracking ID"><Input value={f.tracking_id} onChange={set("tracking_id")} className="font-mono" data-testid="add-tracking" /></F>
          <F label="Ship date"><Input type="date" value={f.ship_date} onChange={set("ship_date")} data-testid="add-ship-date" /></F>
          <F label="Expected delivery"><Input type="date" value={f.expected_delivery} onChange={set("expected_delivery")} data-testid="add-expected" /></F>
          <F label="Status" className="sm:col-span-2">
            <SimpleSelect value={f.status} onChange={set("status")} testid="add-status" className="h-9 w-full"
              options={[["auto", "Automatic (No Update if AWB given, else Not Shipped / No AWB)"], ...statuses.map((s) => [s, s])]} />
          </F>
          <F label="Internal remarks" className="sm:col-span-2"><Textarea rows={2} value={f.remarks} onChange={set("remarks")} data-testid="add-remarks" /></F>
          <DialogFooter className="sm:col-span-2"><Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-save-btn">Add shipment</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
